    import { useCallback } from 'react';
    import usePDA1110_R01Store, { BarcodeInputItem } from './usePDA1110_R01Store';
    import { getBarcodeList, createBarcode } from 'src/apis/barcodeApi';
    import { useToast } from 'src/hooks/useToast';

    const PDA1110_R01Content = () => {
    const { setLoading, barcodeInputList, addBarcodeInput, updateBarcodeInput, reset } = usePDA1110_R01Store();
    const { success, error, warning } = useToast();

    /**
     * 바코드 스캔 처리
     * @returns 조회 성공 여부 (바코드가 DB에 존재하면 true, 없으면 false)
     */
    const handleBarcodeScan = useCallback(async (barcode: string): Promise<boolean> => {
        if (!barcode || barcode.trim() === '') {
        warning('바코드를 입력하세요.');
        return false;
        }

        setLoading(true);
        try {
        const response = await getBarcodeList(barcode.trim());
        
        if (response.data.data && response.data.data.length > 0) {
            // 바코드가 DB에 존재함 - 기존 정보로 업데이트
            const existingInfo = response.data.data[0];
            // 항상 첫 번째 항목 업데이트
            updateBarcodeInput(0, {
                barcode: barcode.trim().toUpperCase(),
                itemCd: existingInfo.itemCd || '',
                itemNm: existingInfo.itemNm,
                itemSpec: existingInfo.itemSpec,
                modelNo: existingInfo.modelNo,
                packQty: existingInfo.packQty || 1,
                itemUnit: existingInfo.itemUnit || '',
                remark: existingInfo.remark,
                isExisting: true,
                isEditable: false,
            });
            success('바코드 정보를 조회했습니다.');
            return true; // ✅ 조회 성공
        } else {
            // 바코드가 DB에 없음 - 빈 정보로 업데이트 (입력 가능)
            updateBarcodeInput(0, {
                barcode: barcode.trim().toUpperCase(),
                itemCd: '',
                itemNm: '',
                itemSpec: '',
                modelNo: '',
                packQty: 1,
                itemUnit: '',
                isExisting: false,
                isEditable: true,
            });
            success('새 바코드입니다. 정보를 입력하세요.');
            return false; // ❌ 조회 실패 (하지만 업데이트는 됨)
        }
        } catch (err: any) {
        console.error('바코드 조회 실패:', err);
        // 에러가 발생해도 새 바코드로 업데이트 (입력 가능)
        updateBarcodeInput(0, {
            barcode: barcode.trim().toUpperCase(),
            itemCd: '',
            itemNm: '',
            itemSpec: '',
            modelNo: '',
            packQty: 1,
            itemUnit: '',
            isExisting: false,
            isEditable: true,
        });
        error(err?.response?.data?.detailMessage || err?.response?.data?.message || '바코드 조회 중 오류가 발생했습니다. 새 바코드로 추가합니다.');
        return false;
        } finally {
        setLoading(false);
        }
    }, [setLoading, updateBarcodeInput, success, error, warning]);

    /**
     * 바코드 저장 (단일 바코드)
     */
    const handleSave = useCallback(async () => {
        if (barcodeInputList.length === 0) {
        warning('저장할 바코드가 없습니다.');
        return;
        }

        // 첫 번째 항목만 저장 (단일 폼)
        const item = barcodeInputList[0];
        
        // 유효성 검증
        if (!item.barcode || item.barcode.trim() === '') {
            error('바코드를 입력하세요.');
            return;
        }
        if (!item.itemCd || item.itemCd.trim() === '') {
            error('품목코드를 입력하세요.');
            return;
        }
        if (!item.packQty || item.packQty <= 0) {
            error('포장수량을 입력하세요.');
            return;
        }

        setLoading(true);
        try {
        const saveData = {
            barcode: item.barcode,
            itemCd: item.itemCd,
            packQty: item.packQty,
            itemUnit: item.itemUnit || '',
            remark: item.remark || ''
        };

        const response = await createBarcode(saveData);
        if (response.data.successFlag) {
            success('바코드가 저장되었습니다.');
            // 저장 후 폼 초기화 (빈 폼으로)
            updateBarcodeInput(0, {
                barcode: '',
                itemCd: '',
                itemNm: '',
                itemSpec: '',
                modelNo: '',
                packQty: 1,
                itemUnit: '',
                remark: '',
                isExisting: false,
                isEditable: true,
            });
        } else {
            error(response.data.message || '바코드 저장에 실패했습니다.');
        }
        } catch (err: any) {
        console.error('바코드 저장 실패:', err);
        error(err?.response?.data?.detailMessage || err?.response?.data?.message || '바코드 저장 중 오류가 발생했습니다.');
        } finally {
        setLoading(false);
        }
    }, [barcodeInputList, setLoading, updateBarcodeInput, success, error, warning]);

    return {
        handleBarcodeScan,
        handleSave
    };
    };

    export default PDA1110_R01Content;
